import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAccountActiveSearchForCaseResults } from '@hooks/react-query/queries/useGetAccountActiveSearchForCaseResults';
import {
  Box,
  Checkbox,
  CheckCircleIcon,
  Field,
  FieldTypes,
  ListItemButton,
  ListItemIcon,
  Loader,
  PlusIcon,
  SearchIcon,
  Stack,
  Typography,
  useTheme,
} from 'ui';
import { RowListWithActions } from '@components/RowListWithActions';
import {
  StyledList,
  StyledListItem,
} from '@components/RowListWithActions/RowListWithActions.styles';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { useInvitationsModalOrchestrator } from '../../InvitationModalsOrchestrator';
import { SelectedUsers, useInviteToCase } from '../InviteToCaseContext';
import {
  CenteredTypography,
  ContentStack,
  FieldContainer,
  ListContainer,
  StyledBox,
  StyledButton,
  StyledFontTypography,
  StyledIcon,
  StyledItemLoader,
} from './SelectUsersStepContent.styles';

export function SelectUsersStepContent() {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedCase, isCurrentOrgAdmin } = useUserOrgCase();

  const { setInviteUserModalOpen, setInviteOrgModalOpen } =
    useInvitationsModalOrchestrator();

  const { setSelectedUsersToInvite } = useInviteToCase();

  const handleInviteToOrg = () => {
    setInviteUserModalOpen(false);
    setInviteOrgModalOpen(true);
  };

  const [checkedUsers, setCheckedUsers] = useState<SelectedUsers[]>([]);
  const [usersToDisplay, setUsersToDisplay] = useState<SelectedUsers[]>([]);

  const handleToggle = useCallback(
    (value: SelectedUsers) => () => {
      setCheckedUsers((prevChecked) => {
        const index = prevChecked.findIndex(
          (user) => user.accountID === value.accountID,
        );
        if (index !== -1) {
          return prevChecked.filter(
            (user) => user.accountID !== value.accountID,
          );
        }
        return [...prevChecked, { ...value, checked: true }];
      });
    },
    [],
  );

  const { data: searchResults, isLoading } =
    useGetAccountActiveSearchForCaseResults({
      caseId: selectedCase.caseID || '',
      searchValue: searchQuery,
    });

  useEffect(() => {
    if (!searchResults) return;

    const mergedUsers: SelectedUsers[] = searchResults.map((account) => ({
      accountID: account.accountID,
      firstName: account.firstName,
      lastName: account.lastName,
      checked: checkedUsers.some(
        (checked) => checked.accountID === account.accountID,
      ),
      accountStatus: '',
      roleName: '' as UserRole,
      email: '',
      phone: '',
    }));

    if (searchQuery.trim() === '') {
      const allUsers = [
        ...checkedUsers.filter(
          (checked) =>
            !mergedUsers.some((user) => user.accountID === checked.accountID),
        ),
        ...mergedUsers,
      ];
      setUsersToDisplay(
        allUsers.sort((a, b) => (b.checked ? 1 : 0) - (a.checked ? 1 : 0)),
      );
    } else {
      setUsersToDisplay(mergedUsers);
    }
    setSelectedUsersToInvite(checkedUsers);
  }, [searchResults, searchQuery, checkedUsers, setSelectedUsersToInvite]);

  const rows = useMemo(() => {
    const usersRows = usersToDisplay?.map((user) => ({
      rowId: user.accountID,
      label: `${user.firstName} ${user.lastName}`,
      showAvatar: true,
      onClick: handleToggle(user),
      secondaryAction: (
        <StyledBox>
          <Checkbox
            aria-label={`Select ${user.firstName} ${user.lastName}`}
            checked={user.checked}
            checkedIcon={<CheckCircleIcon />}
            icon={<Box />}
            label=""
            onChange={() => handleToggle(user)}
          />
        </StyledBox>
      ),
    }));
    return usersRows;
  }, [handleToggle, usersToDisplay]);

  return (
    <Stack>
      <FieldContainer>
        <Field
          iconLeft={<SearchIcon />}
          label="Search"
          onChange={(event) => setSearchQuery(event.target.value)}
          showError={false}
          type={'text' as FieldTypes}
          value={searchQuery}
        />
      </FieldContainer>
      <ListContainer usersList={rows?.length > 0}>
        {rows?.length > 0 ? (
          <>
            <StyledList data-testid="list-container">
              <Stack>
                <RowListWithActions key="selectUsers" rows={rows} />
                {isLoading ? (
                  <StyledItemLoader>
                    <Loader />
                  </StyledItemLoader>
                ) : null}
              </Stack>
            </StyledList>
            {isCurrentOrgAdmin ? (
              <StyledListItem onClick={handleInviteToOrg}>
                <ListItemButton>
                  <ListItemIcon>
                    <PlusIcon color={theme.palette.green[600]} />
                  </ListItemIcon>
                  <StyledFontTypography color="primary" variant="body">
                    Add Organization Member
                  </StyledFontTypography>
                </ListItemButton>
              </StyledListItem>
            ) : null}
          </>
        ) : (
          <StyledList data-testid="list-container">
            {isCurrentOrgAdmin ? (
              <StyledButton onClick={handleInviteToOrg}>
                <StyledIcon color={theme.palette.green[600]} />
                <Typography variant="subtitle">Add to Organization</Typography>
                <Box>
                  <Typography color="text.secondary">
                    Can't find who you're looking for?
                  </Typography>
                  <Typography color="text.secondary">
                    Invite them to your organization.
                  </Typography>
                </Box>
              </StyledButton>
            ) : (
              <ContentStack>
                <StyledIcon color={theme.palette.green[600]} />
                <Typography color="text.secondary">
                  Can't find who you're looking for?
                </Typography>
                <CenteredTypography color="text.secondary">
                  They may already be on this case or not yet part of your
                  organization.
                </CenteredTypography>
                <Typography color="text.secondary">
                  Ask an admin to invite them.
                </Typography>
              </ContentStack>
            )}
          </StyledList>
        )}
      </ListContainer>
    </Stack>
  );
}
